/**
 * Invoice Service
 * Generates tax invoices and tracks AR Collections
 * Framework C.3: Invoice escalation timeline (14-day terms)
 * Mocked implementation - ready for invoice PDF + Xero integration
 */

import { supabase } from '../lib/supabase';
import { invoiceAmounts } from '../lib/invoiceMaths'

export interface InvoiceResult {
  success: boolean;
  message: string;
  details?: {
    invoice_number?: string;
    invoice_id?: string;
    pdf_path?: string;
  };
}

export const invoiceService = {
  /**
   * Generate tax invoice when deal reaches paid status
   * Framework C.3: 14-day payment terms, AR escalation tracking
   */
  generateInvoice: async (
    dealId: string,
    purchasePrice: number,
    /** @deprecated v2.0-NL.2.0 removed the holding deposit; always 0. */
    holdingDeposit: number = 0
  ): Promise<InvoiceResult> => {
    try {
      // Fetch deal details
      const { data: deal, error: dealError } = await supabase
        .from('deals')
        .select('id, buyer_name, buyer_email, buyer_mobile, buyer_company, sale_price, autograb_year, autograb_make, autograb_model')
        .eq('id', dealId)
        .single();

      if (dealError || !deal) {
        throw new Error('Deal not found');
      }

      // Calculate amounts
      /**
       * The invoice is raised on the AGREED SALE PRICE, GST included - not on what the
       * vehicle cost us, and not with ten per cent added on top. Both of those were wrong
       * here before: the caller handed in the AutoGrab estimate or the buy-in figure, and
       * this function then grossed it up. A wholesale price in this business already
       * contains the GST, which is why lib/gstTreatment takes one eleventh out of one.
       *
       * The figure passed in survives only as a fallback for a deal with no sale price
       * recorded, so an invoice can still be raised rather than failing silently at zero.
       */
      const grossSalePrice =
        Number(deal.sale_price ?? 0) > 0 ? Number(deal.sale_price) : Number(purchasePrice) || 0
      const amounts = invoiceAmounts({ gross: grossSalePrice })
      const gstAmount = amounts.gstAmount
      const totalAmount = amounts.total

      // Generate invoice number
      const invoiceNumber = generateInvoiceNumber();

      // Calculate due date (14 days from today - Framework C.3)
      const invoiceDate = new Date();
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 14);

      // Mock generate PDF
      const pdfPath = `invoices/${invoiceNumber}.pdf`;

      // Create invoice record
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert([
          {
            deal_id: dealId,
            invoice_number: invoiceNumber,
            invoice_date: invoiceDate.toISOString(),
            due_date: dueDate.toISOString(),
            buyer_name: deal.buyer_name,
            buyer_email: deal.buyer_email,
            buyer_company: deal.buyer_company,
            buyer_mobile: deal.buyer_mobile,
        purchase_price: amounts.gross,
            holding_deposit: holdingDeposit,
            gst_amount: gstAmount,
            total_amount: totalAmount,
        discount_amount: amounts.discount,
        net_price: amounts.netPrice,
        tax_rate: amounts.taxRate,
            pdf_path: pdfPath,
            status: 'draft'
          }
        ])
        .select('id')
        .single();

      if (invoiceError || !invoice) {
        throw invoiceError || new Error('Failed to create invoice record');
      }

      // Link invoice to deal
      await supabase
        .from('deals')
        .update({ invoice_id: invoice.id })
        .eq('id', dealId);

      console.log(`[INVOICE] Generated ${invoiceNumber} for deal ${dealId}`);

      return {
        success: true,
        message: `Invoice ${invoiceNumber} created`,
        details: {
          invoice_number: invoiceNumber,
          invoice_id: invoice.id,
          pdf_path: pdfPath
        }
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      return {
        success: false,
        message: `Invoice generation failed: ${errorMsg}`
      };
    }
  },

  /**
   * Mark invoice as sent to buyer
   * Framework C.3: Step 1 of AR escalation timeline
   */
  markAsSent: async (invoiceId: string): Promise<InvoiceResult> => {
    try {
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'sent',
          sent_to_buyer_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      if (error) throw error;

      return {
        success: true,
        message: 'Invoice marked as sent'
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      return {
        success: false,
        message: `Mark as sent failed: ${errorMsg}`
      };
    }
  },

  /*
   * processEscalation lived here. It stamped day_minus_1_reminder_sent_at,
   * day_7_formal_demand_sent_at and the rest, and moved the invoice status, without
   * sending anything at all - and nothing ever called it. Anyone who found it later
   * and wired it up would have filled the ledger with demands that were never made.
   *
   * The real escalation path is arService.sendDueReminders: it checks whether the
   * reminder has already gone, sends it, and stamps only then.
   */

  /**
   * Record payment received
   */
  recordPayment: async (
    invoiceId: string,
    amountReceived: number
  ): Promise<InvoiceResult> => {
    try {
      const { data: invoice } = await supabase
        .from('invoices')
        .select('total_amount')
        .eq('id', invoiceId)
        .single();

      const isPaid = amountReceived >= (invoice?.total_amount || 0);

      const { error } = await supabase
        .from('invoices')
        .update({
          payment_received_at: new Date().toISOString(),
          payment_amount_received: amountReceived,
          status: isPaid ? 'paid' : 'overdue'
        })
        .eq('id', invoiceId);

      if (error) throw error;

      return {
        success: true,
        message: isPaid ? 'Invoice marked as paid' : 'Partial payment recorded'
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      return {
        success: false,
        message: `Payment recording failed: ${errorMsg}`
      };
    }
  }
};

/**
 * Generate unique invoice number
 * Format: DL-YYYYMMDD-{sequential}
 */
const generateInvoiceNumber = (): string => {
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
  const sequential = Math.floor(Math.random() * 10000)
    .toString()
    .padStart(4, '0');

  return `DL-${dateStr}-${sequential}`;
};

/**
 * INTEGRATION PLACEHOLDER
 * PDF generation: Use PDFKit or similar
 * import PDFDocument from 'pdfkit';
 *
 * const doc = new PDFDocument();
 * doc.text(`Invoice ${invoiceNumber}`, { fontSize: 24 });
 * doc.text(`Total: $${totalAmount.toFixed(2)}`);
 * doc.text(`Due: ${dueDate.toLocaleDateString('en-AU')}`);
 *
 * // Save to Storage...
 *
 * Xero sync: Already handled in xeroService.ts
 */
